import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import PublicShell from "@/components/PublicShell";
import SeoHead from "@/components/SeoHead";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { setPostAuthRedirect } from "@/lib/post-auth-redirect";
import { isNativeApp, webOrigin } from "@/lib/nativeBridge";
import { toast } from "sonner";
import { formatApiError } from "@/lib/utils-app";
import {
  ShieldCheckIcon as ShieldCheck,
  EyeIcon as Eye,
  CodeIcon as Code,
  ArrowsClockwiseIcon as ArrowsClockwise,
  TrendUpIcon as TrendUp,
  CheckCircleIcon as CheckCircle,
} from "@phosphor-icons/react";

const WHY = [
  {
    icon: ShieldCheck,
    title: "Buyers trust it before they trust you",
    body: "The seal is only conferred after a 7-point identity and business review — buyers know a verified kennel is a real operation with a real person behind it.",
  },
  {
    icon: Eye,
    title: "Stand out in every directory",
    body: "Verified kennels carry the gold seal next to their name across the registry — kennel directory, dog pages, marketplace listings, and search results.",
  },
  {
    icon: Code,
    title: "Put it on your own website",
    body: "Every verified kennel gets an embed kit: copy-paste the official medallion onto your site, linked back to your registry profile.",
  },
  {
    icon: ArrowsClockwise,
    title: "It stays earned",
    body: "Seals are revocable. If a kennel stops meeting the standard, the badge disappears everywhere — including embedded copies. That's why buyers believe it.",
  },
];

const CHECKS = [
  "Government ID verification",
  "Kennel contact + address confirmation",
  "Business / breeding program review",
  "Registered dogs on file",
  "Reference checks from past buyers",
  "Standards attestation",
  "Final human review by registry staff",
];

function PriceCard({ pkg, price, highlight, blurb, features, onBuy, busy, native }) {
  return (
    <div
      className={`rounded-2xl p-7 border flex flex-col ${
        highlight
          ? "bg-[#0E0E0E] text-white border-[#C9A227]/60 shadow-[0_16px_48px_rgba(201,162,39,0.25)]"
          : "bg-white border-[#EAEAEA] shadow-luxury-light"
      }`}
      data-testid={`seal-price-card-${pkg}`}
    >
      {highlight && (
        <span className="self-start text-[10px] font-bold uppercase tracking-[0.25em] bg-gold-gradient text-ink rounded-full px-3 py-1 mb-4">
          Most prestige
        </span>
      )}
      <h3 className="font-spectral text-2xl tracking-tight">{price?.name || pkg}</h3>
      <p className={`text-sm mt-2 leading-relaxed ${highlight ? "text-white/70" : "text-[#6B6B6B]"}`}>{blurb}</p>
      {!native && (
        <p className="font-spectral text-4xl mt-5" data-testid={`seal-price-${pkg}`}>
          {price ? `$${price.amount.toFixed(0)}` : "—"}
          <span className={`text-sm font-sans ${highlight ? "text-white/60" : "text-[#6B6B6B]"}`}>
            {pkg === "elite_verified_annual" ? " / year" : " one-time"}
          </span>
        </p>
      )}
      <ul className="mt-5 space-y-2.5 flex-1">
        {features.map((f) => (
          <li key={f} className="flex items-start gap-2 text-sm">
            <CheckCircle size={16} weight="fill" className="text-[#C9A227] flex-shrink-0 mt-0.5" />
            <span className={highlight ? "text-white/85" : "text-ink"}>{f}</span>
          </li>
        ))}
      </ul>
      {native ? (
        <p className={`mt-7 text-xs ${highlight ? "text-white/60" : "text-[#6B6B6B]"}`}>
          Manage verification from your account on the web.
        </p>
      ) : (
        <Button
          onClick={onBuy}
          disabled={busy}
          className={`mt-7 rounded-full h-11 font-semibold ${
            highlight
              ? "bg-gold-gradient text-ink hover:opacity-95 border-0"
              : "bg-ink hover:bg-crimson text-white"
          }`}
          data-testid={`seal-buy-${pkg}`}
        >
          {busy ? "Redirecting…" : "Get verified"}
        </Button>
      )}
    </div>
  );
}

export default function VerifiedSeal() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [prices, setPrices] = useState({});
  const [busyPkg, setBusyPkg] = useState(null);
  const native = isNativeApp();

  useEffect(() => {
    ["kennel_verification", "elite_verified_annual"].forEach((p) => {
      api.get(`/billing/package-price/${p}`)
        .then((r) => setPrices((prev) => ({ ...prev, [p]: r.data })))
        .catch(() => {});
    });
  }, []);

  const buy = async (packageId) => {
    if (!user) {
      setPostAuthRedirect("/verified-seal");
      toast.info("Create a free account first — you'll come right back here.");
      navigate("/register");
      return;
    }
    setBusyPkg(packageId);
    try {
      const { data } = await api.post("/billing/checkout", {
        package_id: packageId,
        origin_url: webOrigin(),
      });
      if (data.url) {
        window.location.href = data.url;
        return;
      }
      if (data.free) toast.success("Verification unlocked — our team will begin your review.");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusyPkg(null);
    }
  };

  return (
    <PublicShell>
      <SeoHead
        title="The Verified Kennel Seal — Armstrong K9 Registry"
        description="Earn the gold Verified Kennel seal: a 7-point identity and business review that tells buyers your kennel is the real thing."
      />

      {/* Hero */}
      <div className="bg-[#0E0E0E] text-white rounded-2xl p-8 md:p-14 relative overflow-hidden" data-testid="verified-seal-hero">
        <div aria-hidden className="absolute -right-24 -top-24 w-96 h-96 rounded-full bg-[#C9A227]/10 blur-3xl" />
        <div className="relative grid md:grid-cols-[1fr_auto] gap-10 items-center">
          <div className="max-w-xl">
            <span className="text-[10px] font-bold uppercase tracking-[0.3em] text-[#C9A227]">
              Earned. Never bought outright.
            </span>
            <h1 className="font-spectral font-light text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] mt-4">
              The seal buyers look for before they look at your dogs
            </h1>
            <p className="text-white/70 mt-5 leading-relaxed">
              The Verified Kennel seal is conferred after a 7-point identity and
              business review — and revoked if standards slip. That's exactly why
              it works: buyers know it can't be faked.
            </p>
            {!native && (
              <Button
                onClick={() => document.getElementById("seal-pricing")?.scrollIntoView({ behavior: "smooth" })}
                className="mt-7 rounded-full bg-gold-gradient text-ink font-semibold px-7 h-11 border-0 hover:opacity-95"
                data-testid="seal-hero-cta"
              >
                See pricing
              </Button>
            )}
          </div>
          <img
            src="/ak9-seal.png"
            alt="Armstrong K9 Registry verified seal"
            className="w-52 md:w-72 mx-auto select-none drop-shadow-[0_12px_40px_rgba(201,162,39,0.45)]"
            draggable={false}
          />
        </div>
      </div>

      {/* Why it matters */}
      <div className="mt-10 grid sm:grid-cols-2 gap-5">
        {WHY.map(({ icon: Icon, title, body }) => (
          <div key={title} className="bg-white border border-[#EAEAEA] rounded-xl p-6 shadow-luxury-light" data-testid="seal-why-card">
            <Icon size={24} weight="duotone" className="text-crimson" />
            <h3 className="font-spectral text-xl mt-3">{title}</h3>
            <p className="text-sm text-[#6B6B6B] mt-2 leading-relaxed">{body}</p>
          </div>
        ))}
      </div>

      {/* How it works */}
      <div className="mt-10 bg-[#FBF7EC] border border-gold/30 rounded-2xl p-7 md:p-9" data-testid="seal-checks">
        <div className="flex items-center gap-3">
          <img src="/ak9-seal-sm.png" alt="" className="w-10 h-10" draggable={false} />
          <h2 className="font-spectral text-lg md:text-lg tracking-tight">What we verify — all seven, every kennel</h2>
        </div>
        <ol className="mt-5 grid sm:grid-cols-2 gap-x-8 gap-y-2.5">
          {CHECKS.map((c, i) => (
            <li key={c} className="flex items-center gap-3 text-sm">
              <span className="h-6 w-6 rounded-full bg-ink text-white text-[11px] font-bold flex items-center justify-center flex-shrink-0">
                {i + 1}
              </span>
              {c}
            </li>
          ))}
        </ol>
        <p className="text-xs text-[#8A6A00] mt-5">
          Payment starts the review — it never guarantees the badge. If your kennel doesn't pass, our team works with you on what's missing.
        </p>
      </div>

      {/* Pricing */}
      <div id="seal-pricing" className="mt-10">
        <h2 className="font-spectral text-lg md:text-lg tracking-tight mb-5">Choose your level</h2>
        <div className="grid md:grid-cols-2 gap-6">
          <PriceCard
            pkg="kennel_verification"
            price={prices.kennel_verification}
            blurb="One-time identity + business verification. The gold seal on your kennel page and every listing, plus the website embed kit."
            features={[
              "Gold Verified seal across the registry",
              "Website embed kit with revoke-safety",
              "7-point human-reviewed verification",
              "One-time fee — the badge stays while you stay in good standing",
            ]}
            onBuy={() => buy("kennel_verification")}
            busy={busyPkg === "kennel_verification"}
            native={native}
          />
          <PriceCard
            pkg="elite_verified_annual"
            price={prices.elite_verified_annual}
            highlight
            blurb="Annual prestige tier for kennels that want maximum visibility on top of verification."
            features={[
              "Everything in Verified Kennel Badge",
              "Priority placement in the marketplace",
              "Featured slot in the public kennel directory hero",
              "Crimson Elite treatment on your directory card",
            ]}
            onBuy={() => buy("elite_verified_annual")}
            busy={busyPkg === "elite_verified_annual"}
            native={native}
          />
        </div>
        <div className="flex items-center gap-2 mt-6 text-sm text-[#6B6B6B]">
          <TrendUp size={16} className="text-crimson" />
          Verified kennels get shown above unverified ones — and unclaimed listings sit at the bottom.
          <Link to="/kennels" className="underline hover:text-ink" data-testid="seal-browse-kennels-link">
            See the directory
          </Link>
        </div>
      </div>
    </PublicShell>
  );
}
